import { message, notification } from 'antd';
import _ from 'lodash';
import { getIntl } from 'umi';

import { bindServices } from '@/services/api';
import { getFiltersGlobalFilters, toPaginate } from '@/utils/infrastructure';
import { VALIDATION_INTERNAL_ERRORS } from '@/utils/infrastructure/constants';
import CoreQueries from '@/utils/input/queries';

const api = bindServices('manageProcess');
import { queryEditActive, updateFavorite } from './service';
import { filtersConfig } from './utils';

const meta = {
  page: 1,
  limit: 5,
  count: 0,
  totalPages: 0,
};

const initialState = {
  data: [],
  newProcess: false,
  selectedItem: null,
  formConfig: null,
  listUserGroupUser: [],
  pagination: meta,
  view: false,
  filtersConfig,
  orderBy: '',
  id: null,
  favorite: false,
  searchCriteria: '',
  quickAccessOptions: null,
  restoreProcessModal: {
    visible: false,
    message: '',
    processIds: null,
    params: null,
  },
};

const Model = {
  namespace: 'manageProcess',
  state: initialState,
  effects: {
    *fetchList({ payload }, { call, put }) {
      try {
        // Admin view `/processes-manage`: uses `process/manage`
        // (access-aware) so the backend returns the full tenant set to
        // admins and only the visible processes to collaborators,
        // together with the per-record `canManage` flag the table uses
        // to gate configuration buttons.
        const res = yield call(api.getAll, {
          route: 'process/manage',
          ...payload,
          ...CoreQueries.ManageProcess.paged,
          select: `${CoreQueries.ManageProcess.paged.select},canManage`,
        });

        yield put({
          type: 'queryProcess',
          payload: {
            ...res,
            ..._.pick(payload, ['limit', 'page']),
          },
        });
      } catch (e) {}
    },

    *fetchQuickAccessOptions({ payload }, { call, put }) {
      try {
        const res = payload
          ? yield call(api.getById, { route: 'process/quickAccess', ...payload })
          : null;
        yield put({ type: 'queryQuickAccessOptions', payload: res });
      } catch (e) {}
    },

    *bulkUpload({ payload }, { call, put }) {
      try {
        const { fileUploadCallback, ...data } = payload.data;
        const res = yield call(api.update, {
          method: 'post',
          route: `bulkRequestTask/load/${payload.id}`,
          data,
        });

        fileUploadCallback?.();

        if (res.message) {
          const [message, description] = _.split(res.message, '.');
          notification.destroy();
          notification.success({
            ...(message && { message }),
            ...(description && { description }),
            style: { minWidth: 600 },
            duration: 10,
          });
        }

        yield put({ type: 'fetchList', payload: payload.params });
      } catch (e) {}
    },

    *bulkDownload({ payload }, { call }) {
      try {
        message.loading(getIntl().formatMessage({ id: 'process.text.bulk.download.loading' }), 0);
        const res = yield call(api.getById, {
          route: 'process/downloadBulkRequestsTemplate',
          ...payload,
        });

        message.destroy();
        message.success(getIntl().formatMessage({ id: 'process.text.bulk.download.success' }), 5);

        const link = document.createElement('a');
        link.href = `data:${res.contentType};base64,${res.content}`;
        link.download = res.fileName;

        link.click();
        link.remove();
      } catch (e) {
        message.destroy();
      }
    },

    *addNewProcess({ payload }, { put }) {
      yield put({ type: 'addProcess', payload });
      if (!_.isNil(payload.id)) {
        yield put({ type: 'editOrViewProcess', payload });
      }
    },

    *closeNewProcess({ payload }, { put }) {
      yield put({ type: 'closeProcess', payload });
    },

    *editItemProcess({ payload }, { call, put }) {
      try {
        const table = yield call(api.getById, {
          route: 'process',
          id: payload.id,
          params: payload.params,
        });
        yield put({
          type: 'editProcess',
          payload: { ...payload, item: table },
        });
      } catch (e) {
        yield put({ type: 'global/loadingItem', payload: false });
      }
    },

    *saveNewProcess({ payload, params }, { call, put }) {
      try {
        const res = yield call(api.create, {
          route: 'process',
          data: payload,
        });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
        yield put({ type: 'saveProcess', payload: res });
        // Reload the list in the processes model (used by TableGridLayout)
        yield put({ type: 'processes/fetchList', payload: { ...params, readPriority: true } });
      } catch (error) {
        const validationErrors = error?.validationErrors || [];
        const itemDeletedError = validationErrors.find(
          (ve) =>
            ve.errorCode === VALIDATION_INTERNAL_ERRORS.VALIDATION_ITEM_DELETED_EXISTS &&
            ve.metadata?.id,
        );
        if (itemDeletedError) {
          const message =
            (itemDeletedError.errorMessages && itemDeletedError.errorMessages[0]) || error?.message;
          const processId = itemDeletedError.metadata.id;
          yield put({
            type: 'showRestoreProcessModal',
            payload: { message, processId, params },
          });
          return;
        }
        if (error.message) {
          yield put({
            type: 'global/showNotificationsForm',
            payload: {
              visible: true,
              type: 'error',
              message: error.message,
            },
            validationErrors: error.validationErrors,
          });
        }
      }
    },

    *saveEditProcess({ payload, params }, { call, put }) {
      try {
        const res = yield call(api.update, {
          route: 'process',
          data: payload,
        });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
        yield put({ type: 'saveProcess', payload: res });
        yield put({ type: 'processes/fetchList', payload: { ...params, readPriority: true } });
      } catch (error) {
        const validationErrors = error?.validationErrors || [];
        const itemDeletedError = validationErrors.find(
          (ve) =>
            ve.errorCode === VALIDATION_INTERNAL_ERRORS.VALIDATION_ITEM_DELETED_EXISTS &&
            ve.metadata?.id,
        );
        if (itemDeletedError) {
          const message =
            (itemDeletedError.errorMessages && itemDeletedError.errorMessages[0]) || error?.message;
          const processId = itemDeletedError.metadata.id;
          yield put({
            type: 'showRestoreProcessModal',
            payload: { message, processId, params },
          });
          return;
        }
        if (error.message) {
          yield put({
            type: 'global/showNotificationsForm',
            payload: {
              visible: true,
              type: 'error',
              message: error.message,
            },
            validationErrors: error.validationErrors,
          });
        }
      }
    },

    *restoreProcess({ payload }, { call, put, select }) {
      try {
        const { processId } = payload;
        const ids = Array.isArray(processId) ? processId : [processId];
        const state = yield select((s) => s.manageProcess);
        const params = state.restoreProcessModal?.params || { page: 1 };

        yield call(api.apiRoute, {
          route: 'process/restore',
          method: 'post',
          data: ids,
        });
        yield put({ type: 'closeRestoreProcessModal' });
        yield put({
          type: 'addProcess',
          payload: { state: false, item: null },
        });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
        yield put({ type: 'processes/fetchList', payload: { ...params, readPriority: true } });
      } catch (err) {
        if (err?.message) {
          yield put({
            type: 'global/showNotificationsForm',
            payload: { visible: true, type: 'error', message: err.message },
          });
        }
      }
    },

    *deleteItem({ payload }, { call, put, select }) {
      try {
        const { id, view } = payload || {};
        yield put({ type: 'global/modalConfirm', payload: { state: false, id: null } });
        yield call(api.remove, { route: 'process', id });
        yield put({ type: 'saveProcess', payload: {} });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
        const state = yield select((s) => s);
        const processesState = state.processes || {};
        const globalState = state.global || {};
        const pagination = processesState.processListPagination || {};
        const pageSize = globalState.pageSize;
        const globalFiltersStr =
          globalState.globalFilters && typeof globalState.globalFilters === 'object'
            ? getFiltersGlobalFilters(
                globalState.globalFilters.processes || globalState.globalFilters,
                'processes',
              )
            : null;
        const refetchParams = {
          page: pagination.page ?? 1,
          ...(pageSize && { limit: pageSize }),
          ...(processesState.searchDataCriteria && { search: processesState.searchDataCriteria }),
          ...(globalFiltersStr && { filter: globalFiltersStr }),
          readPriority: true,
        };
        yield put({ type: 'processes/fetchList', payload: refetchParams });
      } catch (error) {
        yield put({ type: 'global/modalConfirm', payload: { state: false, id: null } });
        if (error?.message) {
          yield put({
            type: `global/showNotifications${view ? 'Form' : 'Global'}`,
            payload: {
              visible: true,
              type: 'error',
              message: error.message,
              autoHide: view,
            },
          });
        }
      }
    },

    *v1ProcessConfig({ payload }, { call, put }) {
      try {
        const res = yield call(api.apiRoute, { route: `migration/v1ConfigUrl/${payload.id}` });
        yield put({
          type: 'queryV1ProcessConfig',
          payload: { ...payload, ...res },
        });
      } catch (e) {}
    },

    *editActive({ payload, params }, { call, put }) {
      try {
        yield call(queryEditActive, params);
        yield put({ type: 'editActives', payload });
        // Mirror the change into the Launchpad slice so the home-screen
        // tile flips its active/inactive look without requiring a manual
        // refresh. The Launchpad keeps its own copy because it uses a
        // different fetch flow (lazy subgroup loading).
        yield put({
          type: 'processes/updateLaunchpadProcessActive',
          payload: { id: payload.id, isActive: payload.isActive },
        });
      } catch (e) {}
    },

    *setFavorite({ payload }, { put }) {
      yield put({ type: 'queryFavorite' });
      yield put({ type: 'processes/setFavorite', payload });
    },

    *showItemProcesss({ view }, { put }) {
      yield put({ type: 'showEditItemProcess', view });
    },

    *hideAllForms({ payload = 'hideForms' }, { put }) {
      yield put({ type: payload });
    },

    *search({ payload }, { put }) {
      yield put({ type: 'querySearch', payload });
    },

    *setOrderBy({ payload }, { put }) {
      yield put({ type: 'orderBy', payload });
    },

    *eventSorterUserGroup({ payload }, { put }) {
      yield put({ type: 'sorterUserGroup', payload });
    },

    *cleanup({ payload }, { put }) {
      yield put({ type: 'cleanUp' });
    },
  },

  reducers: {
    queryProcess(state, { payload }) {
      return {
        ...state,
        favorite: false,
        data: payload.items,
        pagination: _.merge(state.pagination, toPaginate(payload)),
      };
    },

    queryQuickAccessOptions(state, { payload }) {
      return {
        ...state,
        quickAccessOptions: payload,
      };
    },

    addProcess(state, { payload }) {
      return {
        ...state,
        newProcess: payload.state,
        selectedItem: payload.item,
        view: payload.view,
        id: payload.id,
      };
    },

    editOrViewProcess(state) {
      return {
        ...state,
        selectedItem: {},
      };
    },

    editProcess(state, { payload }) {
      return {
        ...state,
        selectedItem: payload.item,
        view: payload.view,
        id: payload.id,
      };
    },

    closeProcess(state, { payload }) {
      return {
        ...state,
        newProcess: payload.state,
        selectedItem: state.selectedItem,
        view: state.view,
        id: state.id,
      };
    },

    saveProcess(state, { payload }) {
      return {
        ...state,
        saveUser: payload,
        newProcess: false,
        selectedItem: null,
        view: false,
      };
    },

    editActives(state, { payload }) {
      const list = state.list.map((item) => (item.id === payload.id ? payload : item));

      return {
        ...state,
        list,
      };
    },

    showEditItemProcess(state, { view }) {
      return {
        ...state,
        view,
      };
    },

    orderBy(state, { payload }) {
      return {
        ...state,
        orderBy: payload,
      };
    },

    querySearch(state, { payload }) {
      return {
        ...state,
        searchCriteria: payload,
      };
    },

    queryFavorite(state) {
      return {
        ...state,
        favorite: true,
      };
    },

    queryV1ProcessConfig(state, { payload }) {
      return {
        ...state,
        v1ProcessConfig: payload && {
          processId: payload?.id,
          v1ConfigURL: payload?.url,
          metaKey: payload.metaKey,
        },
      };
    },

    // Clears the v1 config payload after it's been consumed by the host
    // component (which navigates to the iframe screen). Without this
    // reset the slice keeps the URL and any subsequent mount of
    // `/processes` would bounce the user straight back to the iframe.
    clearV1ProcessConfig(state) {
      return {
        ...state,
        v1ProcessConfig: null,
      };
    },

    hideForms(state) {
      return {
        ...state,
        newProcess: false,
        newUserOnGroup: false,
        processSearchCriteria: null,
        selectedItem: null,
        id: null,
        orderBy: '',
        view: false,
        searchCriteria: '',
      };
    },

    showRestoreProcessModal(state, { payload }) {
      const processIds =
        payload?.processIds ?? (payload?.processId != null ? [payload.processId] : null);
      return {
        ...state,
        restoreProcessModal: {
          visible: true,
          message: payload?.message || '',
          processIds,
          params: payload?.params ?? null,
        },
      };
    },

    closeRestoreProcessModal(state) {
      return {
        ...state,
        restoreProcessModal: {
          visible: false,
          message: '',
          processIds: null,
          params: null,
        },
      };
    },

    cleanUp() {
      return initialState;
    },
  },
};

export default Model;
